/**
 * T-189 "Link to…": what the dialog says and when it lets you link. Pure, so
 * it is tested on its own (linkCallerFormat.test.ts).
 */

export type LinkKind = 'funeral' | 'lead';

export interface LinkTarget {
  kind: LinkKind;
  id: number | string;
  label: string;
  sub: string | null;
  reason?: string | null;
}

export interface TargetContact {
  clientId: number;
  name: string;
  relationship: string | null;
  phone: string | null;
}

export interface TargetDetail {
  kind: LinkKind;
  id: number | string;
  label: string;
  contacts: TargetContact[];
  canAddContact: boolean;
}

export interface LinkSource {
  phone: string | null;
  fromClient: { id: number; name: string; isStub: boolean };
  fromLead: { id: string; stage: string } | null;
  call: { id: string; channel: string; direction: string; occurredAt: string } | null;
  activeLink: {
    id: string;
    funeralId: number | null;
    leadId: string | null;
    byName: string | null;
    at: string;
  } | null;
}

export interface LinkResult {
  linkId: string;
  personClientId: number;
  personName: string | null;
  funeralId: number | null;
  leadId: string | null;
  movedCommunications: number;
  strayLeadClosed: boolean;
  note: string;
  warnings: string[];
}

/** "new" = a new family contact (or the named caller as themselves); "same" = someone already there. */
export type PersonMode = 'new' | 'same';

export interface PersonChoice {
  mode: PersonMode;
  firstName: string;
  lastName: string;
  relationship: string;
  sameAsClientId: number | null;
}

/** Quick picks under the free-text relationship field — it stays free text, like funeral_clients. */
export const RELATIONSHIP_SUGGESTIONS = [
  'Daughter',
  'Son',
  'Spouse',
  'Sister',
  'Brother',
  'Daughter-in-law',
  'Son-in-law',
  'Grandchild',
  'Friend',
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Only calls and texts logged in `communications` (uuid ids) can be linked one by one. */
export function isLinkableMessageId(id: unknown): id is string {
  return typeof id === 'string' && UUID.test(id);
}

/** "+1 514-555-0123" for a North American number, else as given. */
export function prettyPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const all = phone.replace(/\D/g, '');
  const d = all.length === 11 && all.startsWith('1') ? all.slice(1) : all;
  if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(d)) return phone;
  return `+1 ${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

export function targetName(kind: LinkKind, id: number | string): string {
  return kind === 'funeral' ? `case #${id}` : 'this lead';
}

/** The dialog's subtitle: who is being linked. */
export function callerLine(src: LinkSource, now: Date = new Date()): string {
  const phone = prettyPhone(src.phone);
  const who = src.fromClient.isStub ? phone ?? 'Unknown caller' : [src.fromClient.name, phone].filter(Boolean).join(' · ');
  if (!src.call) return who;
  const what = src.call.channel === 'sms' ? 'text' : 'call';
  return `${who} · ${what} ${formatWhen(src.call.occurredAt, now)}`;
}

export function formatWhen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const tz = 'America/Toronto';
  const day = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: tz });
  const time = d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  if (day(d) === day(now)) return `today ${time}`;
  return `${d.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' })}, ${time}`;
}

/**
 * Move the number to the person by default — except when one call from a
 * NAMED person is being re-attributed (Lorraine on Gordon's phone): then the
 * number may well stay Gordon's, so staff opt in.
 */
export function defaultMoveNumber(src: LinkSource): boolean {
  if (!src.phone) return false;
  return !(src.call && !src.fromClient.isStub);
}

/** Who the "new contact" option adds: the caller themselves when they already have a name. */
export function addsCallerAsIs(src: LinkSource): boolean {
  return !src.fromClient.isStub && !src.call;
}

export function personReady(src: LinkSource, choice: PersonChoice): boolean {
  if (choice.mode === 'same') return choice.sameAsClientId != null;
  return addsCallerAsIs(src) || choice.firstName.trim().length > 0;
}

/** The request body for POST caller-links. */
export function linkBody(
  src: LinkSource,
  target: { kind: LinkKind; id: number | string },
  choice: PersonChoice,
  moveNumber: boolean,
): Record<string, unknown> {
  const person =
    choice.mode === 'same'
      ? { personClientId: choice.sameAsClientId }
      : addsCallerAsIs(src)
        ? { personClientId: src.fromClient.id }
        : { firstName: choice.firstName.trim(), lastName: choice.lastName.trim() || null };
  return {
    ...(src.call ? { communicationId: src.call.id } : { leadId: src.fromLead?.id }),
    ...(target.kind === 'funeral' ? { funeralId: Number(target.id) } : { targetLeadId: String(target.id) }),
    ...person,
    relationship: choice.mode === 'new' ? choice.relationship.trim() || null : null,
    moveNumber,
  };
}

/** The confirmation line after a link. */
export function linkedMessage(r: LinkResult, target: { kind: LinkKind; id: number | string }): string {
  const who = r.personName || 'The caller';
  const where = target.kind === 'funeral' ? `case #${target.id}` : 'the lead';
  const moved =
    r.movedCommunications === 1 ? '1 call or text moved' : `${r.movedCommunications} calls and texts moved`;
  return `${who} is on ${where} now — ${moved}${r.strayLeadClosed ? ', the stray lead is closed' : ''}.`;
}
